import {
  PaymentElement,
  ExpressCheckoutElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { usePopup } from "components/common/wrappers/withPopup";
import React, { useEffect, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import IconLock from "images/lock.svg";
import IconWarn from "images/warning.svg";
import stripeBadge from "images/stripe-badge.webp";
import { Checkbox } from "components/common";
import * as _capitalize from "lodash/capitalize";
import {
  boostProductTierConfig,
  boostTiers,
  BOOST_TERMS_AND_CONDITIONS_URL,
} from "../consts";
import "../BoostLandingStyle.css";
import { calculatePriceAfterPromotion } from "../utils";
import { TierTileCheckoutSnapshot, OrderSummary } from "./BoostPricingHelpers";

const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

const BoostReferralInput = ({
  referral: initialPromoCode,
  setReferral,
  setPromotionCredit,
  setPromotionPercentOff,
  productTerm,
  productTier,
  setCheckoutTerm,
  setCheckoutTier,
  formSubmitting,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isValid, setIsValid] = useState(null);
  const [promoCode, setPromoCode] = useState(initialPromoCode || ""); // local promocode state
  const [promoMessage, setPromoMessage] = useState("");
  const previousSelection = usePrevious({ productTerm, productTier });

  // logics to redirect to yearly
  const [shouldCheckCode, setShouldCheckCode] = useState(false);
  const [showRedirect, setShowRedirect] = useState(false);

  const checkCode = async (code) => {
    if (!code) {
      return;
    }
    /*
      referral code is case sensitive. therefore cannot make every code capitalized
      if code starts with BOOST_, it is assumed to be referral and kept the same
      for anything else, it is assumed promo code and cast to upper case
    */
    const sanitizedCode =
      code.indexOf("BOOST_") === 0 ? code : code.trim().toUpperCase();
    setIsLoading(true);
    setPromoMessage("");
    setShowRedirect(false);
    setPromotionCredit(0);
    setPromotionPercentOff(0);
    try {
      const checkPromoCodePayload = {
        promoOrReferralCode: sanitizedCode,
        productTier,
        productTerm,
      };
      const { data: checkPromoOrReferralCodeResult } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/checkPromoOrReferralCode`,
        checkPromoCodePayload
      );
      const {
        valid = false,
        credit = 0,
        type = "",
        userMessage = `Invalid Promotional Code ${code}`,
      } = checkPromoOrReferralCodeResult || {};
      setIsValid(valid);

      if (valid) {
        setReferral(sanitizedCode);
        if (type === "FIXED" && credit > 0) {
          setPromotionCredit(credit / 100);
          // setPromoMessage(`$${credit / 100} promotional credit applied`);
        }
        if (type === "PERCENT_OFF" && credit > 0) {
          setPromotionPercentOff(credit);
          // setPromoMessage(`${credit}% promotional discount applied`);
        }
      } else {
        setReferral("");
        // this is a special handling logic. should remove after 11/30 as it is not handling future cases
        if (
          ["GET100", "GET50"].includes(sanitizedCode) &&
          userMessage === "Promo Code is Not Eligible For This Product"
        ) {
          setPromoMessage("This Promo Only Applies to select Memberships. ");
          setShowRedirect(true);
        } else {
          setPromoMessage(userMessage);
        }
      }
    } catch (err) {
      setIsValid(false);
      setReferral("");
      setPromotionCredit(0);
      setPromotionPercentOff(0);
      setPromoMessage("Error validating promotional discount");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRedirect = () => {
    const sanitizedPromoCode = promoCode?.toUpperCase();
    setCheckoutTerm("yearly");
    if (sanitizedPromoCode === "GET100") {
      setCheckoutTier("VIP");
    }
    if (sanitizedPromoCode === "GET50") {
      setCheckoutTier("PREMIUM");
    }
    setShouldCheckCode(true);
    setShowRedirect(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    checkCode(promoCode);
    setShouldCheckCode(false);
  }, [shouldCheckCode]);

  // check code from url params or previous entry valid
  useEffect(() => {
    if (
      previousSelection &&
      (previousSelection.productTerm !== productTerm ||
        previousSelection.productTier !== productTier)
    ) {
      setPromoMessage("");
      setShowRedirect(false);
    }
    if (initialPromoCode) {
      checkCode(initialPromoCode);
    }
  }, [initialPromoCode, productTerm, productTier]);

  // clear promotion on coupon input cleared
  useEffect(() => {
    if (!promoCode) {
      setReferral("");
      setPromotionPercentOff(0);
      setPromotionCredit(0);
      setPromoMessage("");
      setShowRedirect(false);
    }
  }, [promoCode]);

  return (
    <>
      <p className="text-gray-700">Promo code (optional)</p>
      <div className="flex flex-row">
        <input
          id="promotionalInput"
          disabled={isLoading || formSubmitting}
          className="flex w-2/3 mb-4 h-8 px-4 border border-gray-300 rounded-lg text-black"
          type="text"
          style={{
            outline: "none",
            minWidth: 220,
            boxShadow: "rgb(86 45 100 / 15%) 13px 11px 36px -10px",
          }}
          placeholder="Enter Code"
          value={promoCode}
          onChange={(e) => setPromoCode(e.target.value?.trim())}
        />
        <button
          type="button"
          disabled={!promoCode || isLoading || formSubmitting}
          id="submit"
          onClick={() => checkCode(promoCode)}
          className={`bg-gray-500 border-2 border-gray-600 ${
            promoCode ? "hover:bg-opacity-75" : ""
          } rounded-lg mb-4 ml-2 h-8 w-1/3 leading-none text-white font-semibold self-center`}
          style={{
            outline: "none",
            boxShadow: "rgb(86 45 100 / 15%) 13px 11px 36px -10px",
          }}
        >
          <span id="promotion-submit" className="whitespace-nowrap">
            {isLoading ? "Checking" : "Apply Promo"}
          </span>
        </button>
      </div>
      <span
        id="promotional-message"
        className={`mb-4 ${isValid ? "text-boost-primary" : "text-red-500"}`}
      >
        {promoMessage}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
        {showRedirect && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
          <span
            className="text-black cursor-pointer underline"
            onClick={handleRedirect}
          >
            Click Here
          </span>
        )}
      </span>
    </>
  );
};

const TierInfoSection = ({
  productTerm,
  productTier,
  belowMd,
  promotionCredit = 0,
  promotionPercentOff = 0,
}) => {
  const selectedConfig =
    boostProductTierConfig[productTerm]?.[productTier] || {};
  const { price } = selectedConfig;

  const promotionApplied = promotionCredit > 0 || promotionPercentOff > 0;

  const priceAfterDiscount = calculatePriceAfterPromotion(
    price,
    promotionPercentOff,
    promotionCredit
  );

  return belowMd ? (
    <div className="flex flex-col justify-center items-center space-y-1">
      <div className="font-bold text-xl" style={{ color: "#87DDA4" }}>
        {productTier} {_capitalize(productTerm)} Plan
      </div>
      <div className="pt-4 flex items-center whitespace-no-wrap">
        <img src={IconLock} alt="" className="inline mr-2" />
        <span className="text-sm">Guaranteed safe & secure checkout.</span>
      </div>
    </div>
  ) : (
    <div className="z-10 sticky" style={{ top: 20 }}>
      <TierTileCheckoutSnapshot
        tier={productTier}
        selectedTerm={productTerm}
        promotionApplied={promotionApplied}
        price={priceAfterDiscount}
        isHighlighted
      />
    </div>
  );
};

const checkBoxLabel = (term = "monthly") => (
  <span>
    I certify that I have read the{" "}
    <a
      href={BOOST_TERMS_AND_CONDITIONS_URL}
      target="_blank"
      rel="noreferrer"
      className="italic hover:text-boost-primary-purple"
    >
      terms and conditions
    </a>{" "}
    of TomoBoost and understand that this is a {term} subscription. I understand
    the refund and cancellation policy and agree to this subscription.
  </span>
);

const MainPaymentSection = ({
  productTerm,
  productTier,
  setCheckoutTerm,
  setCheckoutTier,
  submitDisabled,
  termsAccepted,
  setTermsAccepted,
  handleSubmit,
  isLoading,
  message,
  errorMessageRef,
  paymentElementOptions,
  setStripeLoaded,
  referral,
  setReferral,
  promotionCredit,
  promotionPercentOff,
  setPromotionCredit,
  setPromotionPercentOff,
}) => {
  return (
    <div className="bg-white p-5 md:rounded-lg">
      <form id="payment-form" className="flex flex-col mb-0">
        <PaymentElement
          id="payment-element"
          options={paymentElementOptions}
          onReady={() => setStripeLoaded(true)}
        />
        <BoostReferralInput
          referral={referral}
          setReferral={setReferral}
          setPromotionCredit={setPromotionCredit}
          setPromotionPercentOff={setPromotionPercentOff}
          productTerm={productTerm}
          productTier={productTier}
          setCheckoutTerm={setCheckoutTerm}
          setCheckoutTier={setCheckoutTier}
          formSubmitting={isLoading}
        />
        <OrderSummary
          productTerm={productTerm}
          productTier={productTier}
          promotionCredit={promotionCredit}
          promotionPercentOff={promotionPercentOff}

        />
        <Checkbox
          labelComponent={checkBoxLabel(productTerm)}
          labelClassName="text-sm text-black"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
        />
        <button
          type="button"
          disabled={submitDisabled}
          id="submit"
          onClick={handleSubmit}
          className={`${
            submitDisabled
              ? "bg-gray-secondary"
              : "bg-green-primary hover:bg-opacity-75"
          } mt-10 rounded-lg w-full p-4 leading-none text-white text-xl font-semibold self-center`}
          style={{
            outline: "none",
            minWidth: 220,
            boxShadow: "rgb(86 45 100 / 15%) 13px 11px 36px -10px",
          }}
        >
          <span id="button-submit">{isLoading ? "Processing" : "Submit"}</span>
        </button>
        {/* Show any error or success messages */}
        {(message) && (
          <div
            id="payment-message"
            className="mt-5 py-5 px-5 bg-error-50 text-black rounded-md"
            ref={errorMessageRef}
          >
            <div className="font-bold leading-tight flex items-center">
              <img alt="" src={IconWarn} className="inline mr-1" />
              Payment unsuccessful
            </div>
            <div className="ml-5">
              Please try again or <a className="underline hover:no-underline same-host-redirect" href="mailto:boost@tomocredit.com">contact support.</a>
            </div>
          </div>
        )}
      </form>
      <div className="mx-10 my-5">
        <img src={stripeBadge} alt="stripe-badge" />
      </div>
    </div>
  );
};

const Footer = ({ belowMd }) => {
  return (
    <div
      className={`pb-10 font-extralight leading-none text-white mx-auto ${
        belowMd ? "mt-20" : "mt-32 max-w-3xl"
      } text-sm`}
    />
  );
};

const CheckoutForm = ({
  email,
  productTier = boostTiers.BASIC,
  productTerm = "monthly",
  setCheckoutTerm,
  setCheckoutTier,
  message,
  setMessage,
  setStripeLoaded,
  innerRef,
  errorMessageRef,
  belowMd,
  referralCode,
  promotionCredit,
  setPromotionCredit,
  promotionPercentOff,
  setPromotionPercentOff,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [availableExpressCheckoutMethods, setAvailableExpressCheckoutMethods] = useState([]);
  const [referral, setReferral] = useState(referralCode || "");
  const { hideExitPopup } = usePopup();
  const history = useHistory();

  const submitDisabled = isLoading || !stripe || !elements || !termsAccepted;

  const handleSubmit = async (e) => {
    if (e.preventDefault) {
      e.preventDefault();
    }
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }
    setMessage("");
    setIsLoading(true);
    hideExitPopup();
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setIsLoading(false);
      // setMessage(submitError.message);
      return;
    }
    // create subscription
    let signUpBoostRes;
    try {
      signUpBoostRes = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/boost/createStripeSubscription`,
        { email, productTier, productTerm, referralCode: referral },
      );
    } catch (err) {
      setIsLoading(false);
      setMessage("Error connecting to server");
      return;
    }

    const clientSecret = signUpBoostRes?.data?.clientSecret;
    if (!clientSecret) {
      setMessage("Error connecting to server");
      setIsLoading(false);
      return;
    }

    let confirmPaymentRes;
    try {
      confirmPaymentRes = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          // Make sure to change this to your payment completion page
          return_url: `${
            process.env.REACT_APP_DOMAIN
          }/boost-checkout?email=${encodeURIComponent(
            email,
          )}&tier=${productTier}&term=${productTerm}`,
        },
        // redirect: "if_required",
      });
    } catch (err) {
      setMessage("Error submitting payment");
      setIsLoading(false);
      return;
    }

    const { error } = confirmPaymentRes;
    setIsLoading(false);

    // if(paymentIntent && paymentIntent.status === 'succeeded') {
    //   await axios.post(`${process.env.REACT_APP_BACKEND_URL}/boost/verifyPayment`, { email, clientSecret, paymentIntent: paymentIntent.id });
    //   window.location.assign(`${process.env.REACT_APP_DOMAIN}/dashboard/stripe-redirect?email=${encodeURIComponent(email)}&payment_intent=${paymentIntent.id}&payment_intent_client_secret=${clientSecret}&redirect_status=${paymentIntent.status}`);
    // }

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (error) {
      // show error and collect new card details.
      setMessage(error.message);
    }
  };

  const expressCheckoutOptions = {
    buttonType: {
      applePay: "plain",
      googlePay: "plain",
    },
    buttonTheme: {
      googlePay: "black", // 'black' | 'white'
      applePay: "black", // 'black' | 'white' | 'white-outline'
    },
    wallets: {
      googlePay: "auto", // 'always' | 'auto' | 'never'
      applePay: "auto", // 'always' | 'auto' | 'never'
    },
  };

  const paymentElementOptions = {
    terms: {
      applePay: "never", // 'auto' | 'always' | 'never'
      googlePay: "never", // 'auto' | 'always' | 'never'
      usBankAccount: "never", // 'auto' | 'always' | 'never'
      cashapp: "never", // 'auto' | 'always' | 'never'
      card: "never", // 'auto' | 'always' | 'never'
    },
    wallets: {
      googlePay: "auto", // 'always' | 'auto' | 'never'
      applePay: "auto", // 'always' | 'auto' | 'never'
    },
    fields: {
      billingDetails: {
        name: "auto",
        address: {
          line1: "auto",
          line2: "auto",
          city: "auto",
          state: "auto",
          country: "auto",
          postalCode: "auto",
        },
      },
    },
    // defaultValues: {
    //   billingDetails: {
    //     email,
    //   },
    // },
    layout: {
      type: "accordion",
      defaultCollapsed: false,
      radios: false,
      spacedAccordionItems: true,
    },
  };

  // todo: disable popup if email is used/invalid etc
  const onExpressCheckoutClick = (e) => {
    if (!email) {
      setMessage("Please enter a valid email");
      return;
    }
    const options = {
      emailRequired: true,
    };
    hideExitPopup();
    e.resolve(options);
  };

  return (
    <div
      className={`h-full text-white mx-10 ${belowMd ? "max-w-sm" : ""}`}
      ref={innerRef}
    >
      <div className="mt-5 flex justify-center items-center flex-col md:flex-row">
        <div className="ml-10 self-start md:self-center">
          <button
            type="button"
            className="text-sm font-semibold text-left hover:underline focus:outline-none pml-4"
            onClick={() => {
              const urlParams = new URLSearchParams(window.location.search);
              history.push(`/boost-pricing?${urlParams.toString()}`);
            }}
          >
            ← Back
          </button>
        </div>
        <div className="mt-3 md:mt-0 font-bold mx-auto self-center" style={{ fontSize: 40 }}>Checkout</div>
      </div>
      <div className={`mt-2 md:mt-10 ${!belowMd && "flex justify-between"}`}>
        <div className="xl:bg-black xl:bg-opacity-75 xl:rounded-lg xl:h-full">
          <TierInfoSection
            productTerm={productTerm}
            productTier={productTier}
            promotionCredit={promotionCredit}
            promotionPercentOff={promotionPercentOff}
            belowMd={belowMd}
          />
        </div>
        <div className={`flex flex-col ${belowMd ? "mt-10" : "max-w-md ml-10"}`}>
          <div className="font-semibold text-lg leading-none hidden md:block">Payment Methods</div>
          {!belowMd && <div className="mt-4 flex items-center whitespace-no-wrap">
            <img src={IconLock} alt="" className="inline mr-2" />
            <span className="text-sm">Guaranteed safe & secure checkout.</span>
          </div>}
          <div className="px-5 pt-3 md:px-0 md:pb-3 bg-white md:bg-transparent">
            <div style={{
              maxWidth: availableExpressCheckoutMethods.length ? availableExpressCheckoutMethods.length * 133 + 24 * Math.max(1, availableExpressCheckoutMethods.length - 1) : 448 }}
            >
              <ExpressCheckoutElement
                onClick={onExpressCheckoutClick}
                onConfirm={handleSubmit}
                onReady={(element) => {
                  const { availablePaymentMethods } = element || {};
                  const availableMethods = Object.keys(availablePaymentMethods).filter((pm) => availablePaymentMethods[pm]);
                  setAvailableExpressCheckoutMethods(availableMethods);
                }}
                options={expressCheckoutOptions}
              />
            </div>
          </div>
          <MainPaymentSection
            productTerm={productTerm}
            productTier={productTier}
            setCheckoutTerm={setCheckoutTerm}
            setCheckoutTier={setCheckoutTier}
            submitDisabled={submitDisabled}
            termsAccepted={termsAccepted}
            setTermsAccepted={setTermsAccepted}
            handleSubmit={handleSubmit}
            isLoading={isLoading}
            message={message}
            paymentElementOptions={paymentElementOptions}
            errorMessageRef={errorMessageRef}
            setStripeLoaded={setStripeLoaded}
            setReferral={setReferral}
            referral={referral}
            promotionCredit={promotionCredit}
            promotionPercentOff={promotionPercentOff}
            setPromotionCredit={setPromotionCredit}
            setPromotionPercentOff={setPromotionPercentOff}
          />
        </div>
      </div>
      <Footer belowMd={belowMd} />
    </div>
  );
};

export default CheckoutForm;
